import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { gradeApi } from '../api.js';
import { useAuth } from '../auth/AuthContext';
import ConfirmationModal from './ConfirmationModal';
import SimpleTimezoneSelector from './SimpleTimezoneSelector';

import '../App.css';

// Plan configurations
const PLAN_CONFIGS = {
  free: {
    name: 'Free',
    price: '$0',
    credits: 50,
    color: '#6b7280',
    features: ['20M tokens/month', 'Basic support']
  },
  pro: {
    name: 'Pro',
    price: '$29.99',
    credits: 250,
    color: '#e4b17a',
    features: ['100M tokens/month', 'Priority support', 'Advanced features']
  },
  unlimited: {
    name: 'Unlimited',
    price: 'Custom',
    credits: 1250,
    color: '#10b981',
    features: ['500M tokens/month', 'Premium support', 'All features']
  }
};

const Profile = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const response = await gradeApi.get({
          path: `/api/users/${user.user.id}/`,
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        });
        setProfileData(response.data);
        // console.log('User data fetched:', response.data);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setErrorMessage('Failed to load user data');
      } finally {
        setIsLoading(false);
      }
    };

    const fetchSubscriptionData = async () => {
      try {
        setSubscriptionLoading(true);
        const response = await gradeApi.get({
          path: `/api/subscription/current`,
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        });
        setSubscriptionData(response.data);
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        setErrorMessage('Failed to load subscription data');
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchUserData();
    fetchSubscriptionData();

    // Check for subscription success
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('subscription') === 'success') {
      setShowSuccessModal(true);
      // Clean up the URL
      navigate('/profile', { replace: true });
    }
  }, [user.user.id, location.search, navigate]);

  const handleDeleteUser = () => {
    setIsConfirmationModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (deleteInput.toLowerCase() !== 'delete') {
      setErrorMessage("You must type 'delete' to confirm.");
      return;
    }

    try {
      const response = await gradeApi.delete({
        path: `/api/delete/${user.user.id}/`,
        headers: { Authorization: `Bearer ${user.session_token}` }
      });
      if (response.status === 200) {
        alert('User successfully deleted');
        logout();
      } else {
        setErrorMessage('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setErrorMessage('Error deleting user');
    }
    setIsConfirmationModalOpen(false);
  };

  const handleUpgradeSubscription = async () => {
    try {
      setIsUpgrading(true);
      const response = await gradeApi.post({
        path: `/api/subscription/create-checkout`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      setErrorMessage('Failed to create checkout session');
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const response = await gradeApi.post({
        path: `/api/subscription/cancel`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      if (response.success) {
        alert('Subscription will be canceled at the end of the current period');
        // Refresh subscription data
        const subResponse = await gradeApi.get({
          path: `/api/subscription/current`,
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        });
        setSubscriptionData(subResponse.data);
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setErrorMessage('Failed to cancel subscription');
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/subscription/billing-portal`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      setErrorMessage('Failed to open billing portal');
    }
  };

  const formatDate = dateString => {
    if (!dateString) {
      return 'N/A';
    }
    return new Date(dateString).toLocaleDateString();
  };

  const getProgressBarColor = percentage => {
    // For remaining percentage: low remaining = red, high remaining = green
    if (percentage <= 10) {
      return '#ef4444'; // red for low remaining
    }
    if (percentage <= 30) {
      return '#f97316'; // orange for medium remaining
    }
    return '#22c55e'; // green for high remaining
  };

  if (isLoading) {
    return <div>Loading user data...</div>;
  }

  if (!profileData) {
    return <div>Error: {errorMessage || 'Failed to load user data'}</div>;
  }

  return (
    <div id="Profile" className="flex flex-row p-8 justify-start">
      <div id="Main-Content-container" className="flex flex-col w-full">
        <div
          id="Overview"
          className="flex flex-row space-x-full w-full justify-between sticky top-0 z-10 py-4 mb-4"
          style={{
            background: 'white',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(228, 177, 122, 0.2)'
          }}
        >
          <h1 className="text-2xl font-semibold" style={{ color: '#2d2d2d' }}>
            My Profile
          </h1>
        </div>
        <div className="space-y-6">
          {/* Subscription Management Section */}
          <div
            className="bg-white sexy-border p-6"
            style={{ background: 'linear-gradient(135deg, #fdf8f3 0%, #f9ede2 100%)' }}
          >
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#2d2d2d' }}>
              Subscription & Usage
            </h2>

            {subscriptionLoading ? (
              <div className="flex items-center justify-center py-8">
                <div
                  className="animate-spin rounded-full h-8 w-8 border-b-2"
                  style={{ borderColor: '#e4b17a' }}
                ></div>
                <span className="ml-2" style={{ color: '#a17c57' }}>
                  Loading subscription data...
                </span>
              </div>
            ) : subscriptionData ? (
              <div className="space-y-6">
                {/* Current Plan Card */}
                <div
                  className="p-4 rounded-lg border transition-all duration-300 hover:shadow-lg"
                  style={{
                    background: '#ffffff',
                    borderColor:
                      PLAN_CONFIGS[subscriptionData.subscription.plan]?.color || '#e4b17a',
                    boxShadow: `0 2px 4px -2px rgba(228, 177, 122, 0.06), 0 4px 8px -2px rgba(228, 177, 122, 0.04)`
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor:
                            PLAN_CONFIGS[subscriptionData.subscription.plan]?.color || '#e4b17a'
                        }}
                      ></div>
                      <div>
                        <h3 className="font-semibold text-lg" style={{ color: '#2d2d2d' }}>
                          {PLAN_CONFIGS[subscriptionData.subscription.plan]?.name || 'Free'} Plan
                        </h3>
                        <p className="text-sm" style={{ color: '#a17c57' }}>
                          {subscriptionData.subscription.plan === 'free'
                            ? 'Free forever'
                            : subscriptionData.subscription.plan === 'unlimited'
                              ? 'Enterprise Plan'
                              : `${PLAN_CONFIGS[subscriptionData.subscription.plan]?.price}/month`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium" style={{ color: '#2d2d2d' }}>
                        {subscriptionData.subscription.plan === 'free'
                          ? 'Active'
                          : subscriptionData.subscription.cancel_at_period_end
                            ? 'Expires soon'
                            : subscriptionData.subscription.status}
                      </p>
                      {subscriptionData.subscription.current_period_end && (
                        <p className="text-xs" style={{ color: '#a17c57' }}>
                          {subscriptionData.subscription.cancel_at_period_end ? 'Ends' : 'Renews'}:{' '}
                          {formatDate(subscriptionData.subscription.current_period_end)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Usage Progress */}
                <div
                  className="p-4 rounded-lg"
                  style={{ background: '#ffffff', border: '1px solid rgba(228, 177, 122, 0.2)' }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium" style={{ color: '#2d2d2d' }}>
                      AI Credits Remaining
                    </h4>
                    <span className="text-sm font-medium" style={{ color: '#a17c57' }}>
                      {Math.round(
                        (subscriptionData.usage.credit_limit -
                          subscriptionData.usage.credits_used) *
                          10
                      ) / 10}{' '}
                      / {subscriptionData.usage.credit_limit} credits
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(100 - subscriptionData.usage.percentage_used, 100)}%`,
                        backgroundColor: getProgressBarColor(
                          100 - subscriptionData.usage.percentage_used
                        )
                      }}
                    ></div>
                  </div>
                  <p className="text-xs" style={{ color: '#a17c57' }}>
                    {Math.round(100 - subscriptionData.usage.percentage_used)}% remaining this month
                  </p>
                </div>

                {/* Plan Features */}
                <div
                  className="p-4 rounded-lg"
                  style={{ background: '#ffffff', border: '1px solid rgba(228, 177, 122, 0.2)' }}
                >
                  <h4 className="font-medium mb-2" style={{ color: '#2d2d2d' }}>
                    Current Plan Features
                  </h4>
                  <ul className="space-y-1">
                    {PLAN_CONFIGS[subscriptionData.subscription.plan]?.features.map(
                      (feature, index) => (
                        <li
                          key={index}
                          className="flex items-center text-sm"
                          style={{ color: '#a17c57' }}
                        >
                          <span
                            className="w-2 h-2 rounded-full mr-2"
                            style={{ backgroundColor: '#e4b17a' }}
                          ></span>
                          {feature}
                        </li>
                      )
                    )}
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  {subscriptionData.subscription.plan === 'free' && (
                    <button
                      disabled={isUpgrading}
                      className="flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:transform hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50"
                      style={{
                        background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)'
                      }}
                      onClick={handleUpgradeSubscription}
                    >
                      {isUpgrading ? 'Processing...' : 'Upgrade to Pro'}
                    </button>
                  )}

                  {subscriptionData.subscription.plan === 'pro' && (
                    <button
                      className="flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:transform hover:-translate-y-0.5 hover:shadow-lg"
                      style={{
                        background: 'white',
                        color: '#8c6b4a',
                        border: '1px solid rgba(228, 177, 122, 0.3)',
                        boxShadow: '0 2px 4px rgba(228, 177, 122, 0.1)'
                      }}
                      onClick={handleManageBilling}
                    >
                      Manage Billing
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8" style={{ color: '#a17c57' }}>
                Failed to load subscription data
              </div>
            )}
          </div>

          {/* Profile Information Section */}
          <div
            className="bg-white sexy-border p-6"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #faf8f5 100%)',
              boxShadow:
                '0 2px 4px -2px rgba(228, 177, 122, 0.06), 0 4px 8px -2px rgba(228, 177, 122, 0.04)',
              border: '1px solid rgba(228, 177, 122, 0.15)'
            }}
          >
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#2d2d2d' }}>
              Personal Information
            </h2>

            <div className="grid grid-cols-2 gap-6">
              <div
                className="p-4 rounded-lg border"
                style={{ background: 'white', borderColor: 'rgba(228, 177, 122, 0.2)' }}
              >
                <p className="font-semibold text-sm mb-2" style={{ color: '#c99b69' }}>
                  First Name
                </p>
                <p className="text-lg font-medium" style={{ color: '#2d2d2d' }}>
                  {profileData.first_name}
                </p>
              </div>
              <div
                className="p-4 rounded-lg border"
                style={{ background: 'white', borderColor: 'rgba(228, 177, 122, 0.2)' }}
              >
                <p className="font-semibold text-sm mb-2" style={{ color: '#c99b69' }}>
                  Last Name
                </p>
                <p className="text-lg font-medium" style={{ color: '#2d2d2d' }}>
                  {profileData.last_name}
                </p>
              </div>
              <div
                className="p-4 rounded-lg border"
                style={{ background: 'white', borderColor: 'rgba(228, 177, 122, 0.2)' }}
              >
                <p className="font-semibold text-sm mb-2" style={{ color: '#c99b69' }}>
                  Email
                </p>
                <p className="text-lg font-medium" style={{ color: '#2d2d2d' }}>
                  {profileData.email}
                </p>
              </div>
              <div
                className="p-4 rounded-lg border"
                style={{ background: 'white', borderColor: 'rgba(228, 177, 122, 0.2)' }}
              >
                <p className="font-semibold text-sm mb-2" style={{ color: '#c99b69' }}>
                  Position
                </p>
                <p className="text-lg font-medium" style={{ color: '#2d2d2d' }}>
                  {profileData.is_teacher ? 'Teacher' : 'Student'}
                </p>
              </div>

              {/*{profileData.timezone && profileData.is_teacher && (*/}
              {/*  <div*/}
              {/*    className="p-4 rounded-lg border col-span-2"*/}
              {/*    style={{ background: 'white', borderColor: 'rgba(228, 177, 122, 0.2)' }}*/}
              {/*  >*/}
              {/*    <p className="font-semibold text-sm mb-3" style={{ color: '#c99b69' }}>*/}
              {/*      Timezone*/}
              {/*    </p>*/}
              {/*    <SimpleTimezoneSelector timezone={profileData.timezone} />*/}
              {/*  </div>*/}
              {/*)}*/}
            </div>

            {profileData.student_courses && profileData.student_courses.length > 0 && (
              <div
                className="mt-6 p-4 rounded-lg border"
                style={{ background: 'white', borderColor: 'rgba(228, 177, 122, 0.2)' }}
              >
                <p className="font-semibold text-sm mb-3" style={{ color: '#c99b69' }}>
                  Enrolled Courses
                </p>
                <ul className="space-y-3">
                  {profileData.student_courses.map((course, index) => (
                    <li
                      key={index}
                      className="flex items-center text-base font-medium"
                      style={{ color: '#2d2d2d' }}
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-3"
                        style={{ backgroundColor: '#e4b17a' }}
                      ></span>
                      {course.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                className="px-2 py-1 rounded text-xs font-medium transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-lg"
                style={{
                  background: 'white',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                }}
                onClick={handleDeleteUser}
                onMouseEnter={e => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.05)';
                  e.target.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                }}
                onMouseLeave={e => {
                  e.target.style.background = 'white';
                  e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                }}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        message="Type 'delete' to confirm account deletion. This action cannot be undone."
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={confirmDeleteUser}
      >
        <input
          type="text"
          value={deleteInput}
          placeholder="Type 'delete' to confirm"
          className="mt-2 p-2 border border-gray-300 rounded w-full"
          onChange={e => setDeleteInput(e.target.value)}
        />
        {errorMessage && <p className="text-orange-500 mt-2">{errorMessage}</p>}
      </ConfirmationModal>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl p-8 max-w-md w-full relative"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fdfbf8 100%)',
              boxShadow:
                '0 20px 25px -5px rgba(228, 177, 122, 0.1), 0 10px 10px -5px rgba(228, 177, 122, 0.04)',
              border: '1px solid rgba(228, 177, 122, 0.2)'
            }}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              style={{ color: '#6b7280' }}
              onClick={() => setShowSuccessModal(false)}
            >
              ✕
            </button>

            {/* Success content */}
            <div className="text-center">
              {/* Success icon */}
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #e4b17a, #c99b69)' }}
              >
                <span className="text-white text-2xl">🎉</span>
              </div>

              <h2 className="text-2xl font-bold mb-2" style={{ color: '#2d2d2d' }}>
                Welcome to Pro!
              </h2>

              <p className="text-base mb-6" style={{ color: '#6b7280' }}>
                🚀 Your subscription is now active! You have <strong>250 AI credits</strong> per
                month and access to all Pro features.
              </p>

              {/* Benefits list */}
              <div
                className="text-left mb-6 p-4 rounded-lg"
                style={{ background: 'rgba(228, 177, 122, 0.05)' }}
              >
                <h3 className="font-semibold mb-3" style={{ color: '#c99b69' }}>
                  What you get:
                </h3>
                <ul className="space-y-2 text-sm" style={{ color: '#2d2d2d' }}>
                  <li className="flex items-center">
                    <span
                      className="w-2 h-2 rounded-full mr-3"
                      style={{ backgroundColor: '#e4b17a' }}
                    ></span>
                    100M tokens/month (250 credits)
                  </li>
                  <li className="flex items-center">
                    <span
                      className="w-2 h-2 rounded-full mr-3"
                      style={{ backgroundColor: '#e4b17a' }}
                    ></span>
                    Priority support
                  </li>
                  <li className="flex items-center">
                    <span
                      className="w-2 h-2 rounded-full mr-3"
                      style={{ backgroundColor: '#e4b17a' }}
                    ></span>
                    Advanced features
                  </li>
                  <li className="flex items-center">
                    <span
                      className="w-2 h-2 rounded-full mr-3"
                      style={{ backgroundColor: '#e4b17a' }}
                    ></span>
                    Unlimited assignments
                  </li>
                </ul>
              </div>

              {/* CTA buttons */}
              <div className="space-y-3">
                <a
                  href="https://calendly.com/maxbohun/gradewiz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-6 py-3 rounded-lg font-semibold text-center transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(228, 177, 122, 0.3)'
                  }}
                >
                  📅 Chat with our Founders
                </a>

                <button
                  className="w-full px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-lg"
                  style={{
                    background: 'white',
                    color: '#8c6b4a',
                    border: '1px solid rgba(228, 177, 122, 0.3)',
                    boxShadow: '0 2px 4px rgba(228, 177, 122, 0.1)'
                  }}
                  onClick={() => setShowSuccessModal(false)}
                >
                  Start Using Pro Features
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
